/**
 * Lazy-loaded icons with encapsulated Suspense boundaries.
 */
import React, { Suspense } from 'react';

// oxlint-disable-next-line @atlassian/no-restricted-imports
import { lazyForPaint } from '@atlassian/react-loosely-lazy';

import { withBrandIcon, type BrandIcon } from '../../../icons/with-brand-icon';

const OfficialIcon = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "@atlassian/logo-third-party/salesforce" */ '@atlassian/logo-third-party/salesforce'
		).then((m) => m.SalesforceIcon),
	{ moduleId: '@atlassian/logo-third-party/salesforce' },
);

const LazySalesforceIconComponent = lazyForPaint(
	() => import(/* webpackChunkName: "icon-salesforce" */ './utils').then((m) => m.SalesforceIcon),
	{ moduleId: '@atlassian/rovo-connectors-config/salesforce/icon' },
);

const LazySalesforceSearchResultIconComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-salesforce-search-result" */ './utils').then(
			(m) => m.SalesforceSearchResultIcon,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/salesforce/search-result-icon' },
);

const LazySalesforceIconBorderlessComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-salesforce-borderless" */ './utils').then(
			(m) => m.SalesforceIconBorderless,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/salesforce/icon-borderless' },
);

/**
 * Exported icons with self-contained Suspense boundaries.
 * Consumers can use these directly without wrapping in LazySuspense.
 */
export const SalesforceIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazySalesforceIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'small',
);

export const SalesforceSearchResultIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazySalesforceSearchResultIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'medium',
);

export const SalesforceIconBorderless: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazySalesforceIconBorderlessComponent />
		</Suspense>
	),
	OfficialIcon,
	'xxsmall',
);
